import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Compass, Star, MapPin } from 'lucide-react';
import './AttractionsPage.css';

const DESTINATIONS = [
  { label: 'Dubai',     flag: '\uD83C\uDDE6\uD83C\uDDEA', query: 'Dubai, UAE' },
  { label: 'Paris',     flag: '\uD83C\uDDEB\uD83C\uDDF7', query: 'Paris, France' },
  { label: 'Tokyo',     flag: '\uD83C\uDDEF\uD83C\uDDF5', query: 'Tokyo, Japan' },
  { label: 'New York',  flag: '\uD83C\uDDFA\uD83C\uDDF8', query: 'New York, USA' },
  { label: 'Rome',      flag: '\uD83C\uDDEE\uD83C\uDDF9', query: 'Rome, Italy' },
  { label: 'Istanbul',  flag: '\uD83C\uDDF9\uD83C\uDDF7', query: 'Istanbul, Turkey' },
  { label: 'Maldives',  flag: '\uD83C\uDDF2\uD83C\uDDFB', query: 'Maldives' },
  { label: 'Bangkok',   flag: '\uD83C\uDDF9\uD83C\uDDED', query: 'Bangkok, Thailand' },
  { label: 'Barcelona', flag: '\uD83C\uDDEA\uD83C\uDDF8', query: 'Barcelona, Spain' },
  { label: 'Sydney',    flag: '\uD83C\uDDE6\uD83C\uDDFA', query: 'Sydney, Australia' },
];

export default function AttractionsPage() {
  const navigate = useNavigate();
  const [destIdx,     setDestIdx]     = useState(0);
  const [attractions, setAttractions] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const cache = useRef({});

  useEffect(() => {
    const dest = DESTINATIONS[destIdx];
    if (!dest) return;
    if (cache.current[dest.query]) {
      setAttractions(cache.current[dest.query]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setAttractions([]);
    fetch(`/api/travel/public/attractions?destination=${encodeURIComponent(dest.query)}&limit=12`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        if (!data?.success) throw new Error(data?.message || 'API error');
        const list = Array.isArray(data.data) ? data.data : [];
        cache.current[dest.query] = list;
        setAttractions(list);
      })
      .catch(err => {
        console.error('[AttractionsPage] fetch failed:', err.message);
        setAttractions([]);
      })
      .finally(() => setLoading(false));
  }, [destIdx]);

  const currentDest = DESTINATIONS[destIdx];

  return (
    <>
      <section className="atp-hero">
        <div className="atp-hero-inner">
          <div className="atp-hero-pill">
            <Compass size={12} style={{ verticalAlign: '-1px', marginRight: 5 }} />
            InnBotics Experiences
          </div>
          <h1>Explore World-Class Attractions</h1>
          <p>
            Top-rated sights, landmarks and hidden gems around the globe &mdash;
            powered by Google Places and curated for your stay.
          </p>
          <div className="atp-hero-stats">
            <div className="atp-hero-stat"><strong>10</strong><span>destinations</span></div>
            <div className="atp-hero-stat"><strong>100+</strong><span>real-time attractions</span></div>
            <div className="atp-hero-stat"><strong>Live</strong><span>Google Places data</span></div>
          </div>
        </div>
      </section>

      <div className="atp-body">
        <div className="atp-section-label">Choose a destination</div>
        <div className="atp-tabs">
          {DESTINATIONS.map((d, i) => (
            <button
              key={d.query}
              className={`atp-tab${i === destIdx ? ' atp-tab--active' : ''}`}
              onClick={() => setDestIdx(i)}
            >
              <span className="atp-tab-flag">{d.flag}</span>
              {d.label}
            </button>
          ))}
        </div>

        <div className="atp-grid-header">
          <div>
            <div className="atp-grid-title">
              {currentDest.flag} Top Attractions in {currentDest.label}
            </div>
            {!loading && attractions.length > 0 && (
              <div className="atp-grid-sub">{attractions.length} places found</div>
            )}
          </div>
          <div className="atp-powered">
            <img
              src="https://www.gstatic.com/images/branding/googlelogo/svg/googlelogo_clr_74x24px.svg"
              alt="Google"
              style={{ height: 16, verticalAlign: 'middle' }}
            />
            &nbsp;Places
          </div>
        </div>

        <div className="atp-grid">
          {loading ? (
            [1,2,3,4,5,6,7,8,9,10,11,12].map(i => (
              <div key={i} className="atp-card atp-card--skeleton">
                <div className="atp-img atp-skel" />
                <div className="atp-body-text">
                  <div className="atp-skel atp-skel--title" style={{ marginBottom: 8 }} />
                  <div className="atp-skel atp-skel--sub" />
                </div>
              </div>
            ))
          ) : attractions.length === 0 ? (
            <div className="atp-empty">
              <Compass size={42} color="#d1d5db" />
              <p>No attractions found for this destination.</p>
              <p style={{ fontSize: '0.82rem' }}>Try a different destination above.</p>
            </div>
          ) : (
            attractions.map(a => (
              <div
                key={a.id}
                className="atp-card"
                onClick={() => navigate(`/booking?destination=${encodeURIComponent(currentDest.label)}`)}
              >
                {a.photoUrl ? (
                  <div
                    className="atp-img"
                    style={{ backgroundImage: `url(${a.photoUrl})` }}
                  />
                ) : (
                  <div className="atp-img atp-img--placeholder">
                    <Compass size={40} color="rgba(255,255,255,0.4)" />
                  </div>
                )}
                <div className="atp-overlay" />
                <div className="atp-body-text">
                  {a.type && (
                    <span className="atp-badge">
                      {a.type.replace(/\b\w/g, c => c.toUpperCase())}
                    </span>
                  )}
                  <div className="atp-name">{a.name}</div>
                  {a.rating != null && (
                    <div className="atp-rating">
                      <Star size={12} fill="#febb02" color="#febb02" />
                      <span>{a.rating.toFixed(1)}</span>
                      {a.reviewCount != null && (
                        <span className="atp-reviews">({a.reviewCount.toLocaleString()})</span>
                      )}
                    </div>
                  )}
                  {a.address && (
                    <div className="atp-addr">
                      <MapPin size={10} />
                      {a.address.length > 46 ? a.address.slice(0, 46) + '...' : a.address}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="atp-cta">
          <div className="atp-cta-text">
            <h3>Ready to visit {currentDest.label}?</h3>
            <p>Find hotels near every attraction &mdash; book your stay with InnBotics and let our concierge handle the rest.</p>
          </div>
          <button
            className="atp-cta-btn"
            onClick={() => navigate(`/booking?destination=${encodeURIComponent(currentDest.label)}`)}
          >
            Find Hotels in {currentDest.label}
          </button>
        </div>
      </div>
    </>
  );
}